import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Download, Map, Clock, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { getTranslation } from '../i18n/translations';

const Home = () => {
  const navigate = useNavigate();
  const { whiteLabel, lockers, language } = useAuth();
  const primaryColor = whiteLabel ? '#2D4A8F' : '#F26724';
  const brandName = whiteLabel ? 'Smart Locker System' : 'Swedbank Lockers';
  
  const t = (key, replacements) => getTranslation(key, language, replacements);

  const freeLockers = lockers.filter(l => l.state === 'Free' && l.type !== 'Keypad' && l.type !== 'Empty').length;
  const occupiedLockers = lockers.filter(l => l.state === 'Occupied').length;
  const totalLockers = lockers.filter(l => l.type !== 'Keypad' && l.type !== 'Empty').length;

  return (
    <div className="space-y-8">
      <div className="text-center space-y-4 py-8">
        <h1 className="text-5xl font-bold text-[#1A1A1A]">
          {t('welcomeTo')} <span style={{ color: primaryColor }}>{brandName}</span>
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          {t('smartSecure')}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        <Card 
          className="cursor-pointer transition-all hover:shadow-xl hover:-translate-y-1 border-2"
          onClick={() => navigate('/deposit')}
          style={{ borderColor: primaryColor }}
        >
          <CardHeader>
            <div className="flex items-center justify-center w-16 h-16 rounded-full mb-4 mx-auto" style={{ backgroundColor: `${primaryColor}20` }}>
              <Upload size={32} style={{ color: primaryColor }} />
            </div>
            <CardTitle className="text-2xl text-center">{t('sendParcel')}</CardTitle>
            <CardDescription className="text-center">
              {t('depositYourPackage')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="w-full text-lg py-6" 
              style={{ backgroundColor: primaryColor }}
            >
              {t('startDeposit')}
            </Button>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer transition-all hover:shadow-xl hover:-translate-y-1 border-2"
          onClick={() => navigate('/retrieve')}
          style={{ borderColor: primaryColor }}
        >
          <CardHeader>
            <div className="flex items-center justify-center w-16 h-16 rounded-full mb-4 mx-auto" style={{ backgroundColor: `${primaryColor}20` }}>
              <Download size={32} style={{ color: primaryColor }} />
            </div>
            <CardTitle className="text-2xl text-center">{t('receiveParcel')}</CardTitle>
            <CardDescription className="text-center">
              {t('retrieveYourPackage')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="w-full text-lg py-6" 
              style={{ backgroundColor: primaryColor }}
            >
              {t('startRetrieval')}
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        <Card className="text-center">
          <CardHeader>
            <Map size={32} className="mx-auto mb-2" style={{ color: primaryColor }} />
            <CardTitle>{t('lockerStatus')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#1A1A1A]">
              {freeLockers} / {totalLockers}
            </div>
            <p className="text-sm text-gray-600 mt-2">{t('availableLockers')}</p>
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => navigate('/lockers')}
            >
              {t('viewMap')}
            </Button>
          </CardContent>
        </Card>

        <Card className="text-center">
          <CardHeader>
            <Clock size={32} className="mx-auto mb-2" style={{ color: primaryColor }} />
            <CardTitle>{t('timeline')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#1A1A1A]">
              {t('live')}
            </div>
            <p className="text-sm text-gray-600 mt-2">{t('realTimeActivity')}</p>
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => navigate('/admin')}
            >
              {t('viewTimeline')}
            </Button>
          </CardContent>
        </Card>

        <Card className="text-center">
          <CardHeader>
            <Info size={32} className="mx-auto mb-2" style={{ color: primaryColor }} />
            <CardTitle>{t('howItWorks')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-gray-600 space-y-2 text-left">
              <p>1. {t('selectSize')}</p>
              <p>2. {t('generatePin')}</p>
              <p>3. {t('deposit')} / {t('retrieve')}</p>
              <p>4. Simple and secure</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="max-w-4xl mx-auto" style={{ borderLeft: `4px solid ${primaryColor}` }}>
        <CardHeader>
          <CardTitle>{t('aboutDemo')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600">
            {t('aboutText', { brand: brandName })}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Home;
